import React, { useCallback, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { TAX_SUMMARY_MONTH_KEYS, TAX_SUMMARY_MONTH_LABELS, formatTaxSummaryAmount } from '@/lib/taxSummaryWorkbookConstants';
import TaxSummaryAmountInput from '@/components/despacho/TaxSummaryAmountInput';
import {
  INPC_MONTH_KEYS,
  calcularActualizacionPerdida,
  ensureIndicesInpc,
  saldoPendienteDesdeActualizado,
} from '@/lib/taxSummaryPerdidasInpc';

const PERDIDAS_MONTH_KEYS = TAX_SUMMARY_MONTH_KEYS.filter((k) => k !== 'acumulado');

const RETENCION_DETAIL_ROW_IDS = new Set([
  'retenciones.resico',
  'retenciones.fletes',
  'retenciones.honorarios',
  'retenciones.arrendamiento',
  'retenciones.iva_terceros',
  'retenciones.ispt_salarios',
]);

function formatMoney(value, amountMode = 'money') {
  return formatTaxSummaryAmount(value, amountMode) || '—';
}

function rowHasPositiveAmount(monthMap) {
  if (!monthMap || typeof monthMap !== 'object') return false;
  return PERDIDAS_MONTH_KEYS.some((mk) => {
    const n = Number(String(monthMap[mk] ?? '').replace(/,/g, ''));
    return Number.isFinite(n) && Math.abs(n) > 0.005;
  });
}

/** Solo filas de retención con monto; Total Retenciones siempre queda. */
export function filterVisorRetencionRows(rowDefs = [], values = {}) {
  return (rowDefs || []).filter((def) => {
    const rid = def?.row_id || '';
    if (!RETENCION_DETAIL_ROW_IDS.has(rid)) return true;
    return rowHasPositiveAmount(values[rid]);
  });
}

/** Título de sección (sin columnas de meses / sin «—»). */
export function isVisorSectionTitle(def = {}) {
  const kind = String(def.kind || '').toLowerCase();
  if (kind === 'section') return true;
  const rid = String(def.row_id || '');
  if (rid === 'visor.datos_declaracion') return true;
  const label = String(def.label || '');
  return /datos\s+para\s+la\s+declaraci[oó]n\s+provisional/i.test(label);
}

function emptyMonths() {
  return Object.fromEntries(TAX_SUMMARY_MONTH_KEYS.map((k) => [k, '']));
}

function MonthGridSheet({
  title,
  description,
  rowDefs = [],
  values = {},
  onChange,
  readOnly = false,
  amountMode = 'money',
}) {
  const updateCell = (rowId, monthKey, raw) => {
    if (readOnly || !onChange) return;
    const nextValues = { ...values };
    const row = { ...(nextValues[rowId] || {}) };
    row[monthKey] = raw;
    nextValues[rowId] = row;
    onChange(nextValues);
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[200px] text-xs">Concepto</TableHead>
              {TAX_SUMMARY_MONTH_KEYS.map((mk) => (
                <TableHead key={mk} className="text-xs text-center whitespace-nowrap min-w-[68px]">
                  {TAX_SUMMARY_MONTH_LABELS[mk]}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rowDefs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={1 + TAX_SUMMARY_MONTH_KEYS.length} className="text-center text-muted-foreground text-sm py-6">
                  Sin filas definidas.
                </TableCell>
              </TableRow>
            ) : (
              rowDefs.map((def, idx) => {
                const kind = def.kind || 'row';
                const rowId = def.row_id || `row-${idx}`;
                if (kind === 'blank') {
                  return (
                    <TableRow key={rowId} className="h-3">
                      <TableCell
                        colSpan={1 + TAX_SUMMARY_MONTH_KEYS.length}
                        className="p-1 border-0"
                      />
                    </TableRow>
                  );
                }
                if (isVisorSectionTitle(def)) {
                  return (
                    <TableRow
                      key={rowId}
                      className="bg-slate-100/90 hover:bg-slate-100/90"
                      data-kind="section"
                    >
                      <TableCell
                        colSpan={1 + TAX_SUMMARY_MONTH_KEYS.length}
                        className="text-xs font-semibold py-2.5 text-slate-800"
                      >
                        {def.label}
                      </TableCell>
                    </TableRow>
                  );
                }
                const row = values[rowId] || {};
                const isTotal = String(def.label || '').toLowerCase().startsWith('total');
                return (
                  <TableRow key={rowId} className={isTotal ? 'bg-slate-50/80' : undefined}>
                    <TableCell className={`text-xs ${isTotal ? 'font-semibold' : 'font-medium'}`}>
                      {def.label}
                    </TableCell>
                    {TAX_SUMMARY_MONTH_KEYS.map((mk) => (
                      <TableCell key={`${rowId}-${mk}`} className="p-1">
                        {readOnly ? (
                          <span className={`text-xs tabular-nums px-1 block text-right ${isTotal ? 'font-semibold' : ''}`}>
                            {row[mk] ? formatMoney(row[mk], amountMode) : '—'}
                          </span>
                        ) : (
                          <TaxSummaryAmountInput
                            className="h-7 text-xs text-right"
                            value={row[mk] ?? ''}
                            editable={!readOnly}
                            amountMode={amountMode}
                            onChange={(next) => updateCell(rowId, mk, next)}
                          />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function InpcIndicesTable({ indices = {}, onChange, readOnly = false, workbookYear }) {
  const years = useMemo(
    () => Object.keys(indices || {}).sort((a, b) => Number(a) - Number(b)),
    [indices],
  );

  const setCell = (year, monthKey, raw) => {
    if (readOnly) return;
    onChange({
      ...indices,
      [String(year)]: {
        ...(indices[String(year)] || {}),
        [monthKey]: raw,
      },
    });
  };

  const addYear = () => {
    if (readOnly) return;
    const nums = years.map(Number).filter((n) => Number.isFinite(n));
    const nextYear = (nums.length ? Math.max(...nums) : Number(workbookYear) || new Date().getFullYear()) + 1;
    onChange({
      ...indices,
      [String(nextYear)]: Object.fromEntries(INPC_MONTH_KEYS.map((mk) => [mk, ''])),
    });
  };

  return (
    <div
      className="rounded-sm border border-slate-200 bg-white"
      data-testid="tax-summary-inpc-table"
    >
      <Accordion type="single" collapsible defaultValue="">
        <AccordionItem value="inpc" className="border-0">
          <AccordionTrigger
            compact
            className="px-4 py-3 text-sm font-semibold text-[#0F2D5C] hover:no-underline"
            data-testid="tax-summary-inpc-toggle"
          >
            <span className="flex flex-col items-start gap-0.5 text-left pr-2">
              <span>Índices inflacionarios (INPC)</span>
              <span className="text-[11px] font-normal text-slate-500">
                Plegable · semilla hasta 10 años (Art. 57). Abra para editar o completar meses de INEGI.
              </span>
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-3">
            <p className="text-[11px] text-slate-500 m-0 mb-2">
              Se usan jul/dic del año de origen y jun del año del libro. Complete meses vacíos cuando
              INEGI publique el índice.
            </p>
            <div className="overflow-x-auto space-y-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px] w-14">Año</TableHead>
                    {INPC_MONTH_KEYS.map((mk) => (
                      <TableHead key={mk} className="text-[10px] text-center px-1">
                        {TAX_SUMMARY_MONTH_LABELS[mk]}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {years.map((year) => (
                    <TableRow key={year}>
                      <TableCell className="text-xs font-medium tabular-nums py-1">{year}</TableCell>
                      {INPC_MONTH_KEYS.map((mk) => (
                        <TableCell key={mk} className="p-1">
                          <Input
                            className="h-7 text-[10px] tabular-nums text-right px-1"
                            value={(indices[year] || {})[mk] ?? ''}
                            readOnly={readOnly}
                            inputMode="decimal"
                            placeholder="—"
                            data-testid={`tax-summary-inpc-${year}-${mk}`}
                            onChange={(e) => setCell(year, mk, e.target.value)}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!readOnly ? (
                <Button type="button" variant="outline" size="sm" onClick={addYear} data-testid="tax-summary-inpc-add-year">
                  <Plus className="h-4 w-4 mr-1" />
                  Agregar año INPC
                </Button>
              ) : null}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function PerdidasSheet({
  entries = [],
  onChange,
  readOnly = false,
  indicesInpc = {},
  onIndicesChange,
  workbookYear,
}) {
  const indices = useMemo(
    () => ensureIndicesInpc(indicesInpc, workbookYear),
    [indicesInpc, workbookYear],
  );

  const updateEntry = (idx, field, raw) => {
    if (readOnly) return;
    const next = entries.map((e, i) => {
      if (i !== idx) return e;
      const patched = { ...e, [field]: raw };
      if (field === 'saldo_pendiente') {
        patched.saldo_pendiente_manual = '1';
      }
      return patched;
    });
    onChange(next);
  };

  const updateAplicado = (idx, monthKey, raw) => {
    if (readOnly) return;
    const next = entries.map((e, i) => {
      if (i !== idx) return e;
      return {
        ...e,
        aplicado: { ...(e.aplicado || emptyMonths()), [monthKey]: raw },
        // Al cambiar aplicado, el saldo vuelve a calcularse solo.
        saldo_pendiente_manual: '',
      };
    });
    onChange(next);
  };

  const addRow = () => {
    onChange([
      ...entries,
      {
        id: `perdida-${Date.now()}`,
        anio_origen: '',
        monto_inicial: '',
        saldo_pendiente: '',
        saldo_pendiente_manual: '',
        aplicado: emptyMonths(),
      },
    ]);
  };

  const removeRow = (idx) => {
    onChange(entries.filter((_, i) => i !== idx));
  };

  const handleIndicesChange = (next) => {
    onIndicesChange?.(next);
  };

  return (
    <div className="space-y-4">
      <InpcIndicesTable
        indices={indices}
        onChange={handleIndicesChange}
        readOnly={readOnly}
        workbookYear={workbookYear}
      />

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Pérdidas fiscales acumuladas</CardTitle>
          <CardDescription>
            Capture año de origen y monto inicial; la actualización Art. 57 usa los INPC de arriba.
            Lo aplicado por mes se vuelca a ISR general → Pérdidas fiscales al recalcular.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto space-y-3">
          {entries.length === 0 ? (
            <p className="text-sm text-muted-foreground m-0">
              Sin pérdidas registradas. Agrega una fila para capturar año de origen y montos aplicados por mes.
            </p>
          ) : (
            entries.map((entry, idx) => {
              const calc = calcularActualizacionPerdida({
                montoInicial: entry.monto_inicial,
                anioOrigen: entry.anio_origen,
                anioAplicacion: workbookYear,
                indices,
                ultimaActAnio: entry.ultima_act_anio,
                ultimaActMes: entry.ultima_act_mes,
              });
              const saldoAuto = saldoPendienteDesdeActualizado(entry, calc.montoActualizado);
              const saldoShown = String(entry.saldo_pendiente_manual || '').trim()
                ? (entry.saldo_pendiente ?? '')
                : (saldoAuto || entry.saldo_pendiente || '');
              return (
                <div
                  key={entry.id || `perd-${idx}`}
                  className="rounded-md border border-slate-200 p-3 space-y-2"
                  data-testid={`tax-summary-perdida-row-${idx}`}
                >
                  <div className="flex flex-wrap gap-2 items-end">
                    <div className="space-y-1">
                      <span className="text-[10px] text-slate-500 uppercase">Año origen</span>
                      <Input
                        className="h-8 text-xs w-24"
                        value={entry.anio_origen ?? ''}
                        readOnly={readOnly}
                        onChange={(e) => updateEntry(idx, 'anio_origen', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] text-slate-500 uppercase">Monto inicial</span>
                      <TaxSummaryAmountInput
                        className="h-8 text-xs w-32 text-right"
                        value={entry.monto_inicial ?? ''}
                        editable={!readOnly}
                        onChange={(next) => updateEntry(idx, 'monto_inicial', next)}
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] text-slate-500 uppercase">Factor 1ª act.</span>
                      <Input
                        className="h-8 text-xs w-24 tabular-nums bg-slate-50"
                        value={calc.factorPrimera}
                        readOnly
                        data-testid={`tax-summary-perdida-factor1-${idx}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] text-slate-500 uppercase">Monto 1ª act.</span>
                      <Input
                        className="h-8 text-xs w-32 tabular-nums text-right bg-slate-50"
                        value={formatTaxSummaryAmount(calc.montoPrimera) || calc.montoPrimera || '—'}
                        readOnly
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] text-slate-500 uppercase">Factor subsecuente</span>
                      <Input
                        className="h-8 text-xs w-24 tabular-nums bg-slate-50"
                        value={calc.factorSubsecuente || '—'}
                        readOnly
                        data-testid={`tax-summary-perdida-factor2-${idx}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] text-slate-500 uppercase">Monto actualizado</span>
                      <Input
                        className="h-8 text-xs w-32 tabular-nums text-right bg-emerald-50 border-emerald-200"
                        value={formatTaxSummaryAmount(calc.montoActualizado) || calc.montoActualizado || '—'}
                        readOnly
                        data-testid={`tax-summary-perdida-actualizado-${idx}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] text-slate-500 uppercase">Saldo pendiente</span>
                      <TaxSummaryAmountInput
                        className="h-8 text-xs w-32 text-right"
                        value={saldoShown}
                        editable={!readOnly}
                        onChange={(next) => updateEntry(idx, 'saldo_pendiente', next)}
                      />
                    </div>
                    {!readOnly ? (
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeRow(idx)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    ) : null}
                  </div>
                  {calc.missing?.length ? (
                    <p className="text-[10px] text-amber-800 m-0">
                      Faltan INPC para calcular: {calc.missing.join(', ')}. Complételos en la tabla de arriba.
                    </p>
                  ) : null}
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-1">
                    {PERDIDAS_MONTH_KEYS.map((mk) => (
                      <div key={mk} className="space-y-0.5">
                        <span className="text-[10px] text-slate-500">{TAX_SUMMARY_MONTH_LABELS[mk]}</span>
                        <TaxSummaryAmountInput
                          className="h-7 text-xs text-right"
                          value={(entry.aplicado || {})[mk] ?? ''}
                          editable={!readOnly}
                          onChange={(next) => updateAplicado(idx, mk, next)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
          {!readOnly ? (
            <Button type="button" variant="outline" size="sm" onClick={addRow}>
              <Plus className="h-4 w-4 mr-1" />
              Agregar pérdida
            </Button>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

export default function TaxSummaryAuxiliarySheetsEditor({
  auxiliarySheets = {},
  auxiliaryLayout = {},
  readOnly = false,
  onAuxiliaryChange,
  workbookYear,
}) {
  const visor = auxiliarySheets.visor_fiscal || { values: {} };
  const diferencias = auxiliarySheets.diferencias || { values: {} };
  const perdidas = auxiliarySheets.perdidas_acumuladas || { entries: [], indices_inpc: {} };

  const setVisorValues = useCallback((values) => {
    onAuxiliaryChange({
      ...auxiliarySheets,
      visor_fiscal: { ...visor, values },
    });
  }, [auxiliarySheets, onAuxiliaryChange, visor]);

  const setDiferenciasValues = useCallback((values) => {
    onAuxiliaryChange({
      ...auxiliarySheets,
      diferencias: { ...diferencias, values },
    });
  }, [auxiliarySheets, diferencias, onAuxiliaryChange]);

  const setPerdidasEntries = useCallback((entries) => {
    onAuxiliaryChange({
      ...auxiliarySheets,
      perdidas_acumuladas: { ...perdidas, entries },
    });
  }, [auxiliarySheets, onAuxiliaryChange, perdidas]);

  const setIndicesInpc = useCallback((indices_inpc) => {
    onAuxiliaryChange({
      ...auxiliarySheets,
      perdidas_acumuladas: { ...perdidas, indices_inpc },
    });
  }, [auxiliarySheets, onAuxiliaryChange, perdidas]);

  const visorRowDefs = useMemo(
    () => filterVisorRetencionRows(auxiliaryLayout.visor_fiscal || [], visor.values || {}),
    [auxiliaryLayout.visor_fiscal, visor.values],
  );

  return (
    <div className="space-y-4">
      <Tabs defaultValue="visor">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="visor">Calculo Declaracion Prov</TabsTrigger>
          <TabsTrigger value="diferencias">Diferencias (manual vs Sistema)</TabsTrigger>
          <TabsTrigger value="perdidas">Pérdidas Fiscales</TabsTrigger>
        </TabsList>
        <TabsContent value="visor" className="mt-4">
          <MonthGridSheet
            title="Calculo Declaracion Prov"
            description="Sincronizado desde la hoja 1 al recalcular. Incluye impuestos a enterar y datos para capturar la provisional (por régimen). Montos en pesos enteros. Solo lectura."
            rowDefs={visorRowDefs}
            values={visor.values || {}}
            onChange={setVisorValues}
            readOnly
            amountMode="pesos"
          />
        </TabsContent>
        <TabsContent value="diferencias" className="mt-4">
          <MonthGridSheet
            title="Diferencias (manual vs Sistema)"
            description="Δ por mes = captura manual − precarga del sistema, en el impuesto causado de cada rubro (ISR, IVA, retenciones y ventas). Se actualiza al recalcular. Solo lectura."
            rowDefs={auxiliaryLayout.diferencias || []}
            values={diferencias.values || {}}
            onChange={setDiferenciasValues}
            readOnly
            amountMode="money"
          />
        </TabsContent>
        <TabsContent value="perdidas" className="mt-4">
          <PerdidasSheet
            entries={perdidas.entries || []}
            onChange={setPerdidasEntries}
            readOnly={readOnly}
            indicesInpc={perdidas.indices_inpc || {}}
            onIndicesChange={setIndicesInpc}
            workbookYear={workbookYear}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
